/**
 * Settings modal shared by the delivery-step tabs. Opened from each tab's
 * Settings toolbar action; edits are applied to the parent form on Done. Each
 * tab passes a distinct `dialogId` / `idPrefix` so multiple modals coexist.
 */
import { useState } from "react";
import { closeModal, Modal } from "risa-oasis-ui_v2";
import { TrackStatusSettings } from "../../../../../data-model/deliveryStepsConfig";
import StepSettingsCard from "./stepSettingsCard";

export interface StepSettingsFormValues {
  enabled: boolean;
  action: string;
  statusField: string;
  settings: TrackStatusSettings;
}

interface StepSettingsModalProps {
  dialogId: string;
  idPrefix: string;
  title: string;
  enabled: boolean;
  action: string;
  statusField: string;
  settings: TrackStatusSettings;
  showBatchSettings?: boolean;
  onApply: (values: StepSettingsFormValues) => void;
}

/**
 * General (+ optional batch) settings modal for a delivery step.
 *
 * @param dialogId - Unique modal dialog id for this tab
 * @param idPrefix - Element id prefix passed to the settings card
 * @param title - Modal heading
 * @param enabled - Current enabled flag from the parent form
 * @param action - Current action from the parent form
 * @param statusField - Current status source field from the parent form
 * @param settings - Current batch settings from the parent form
 * @param showBatchSettings - Whether to render the Batch Settings card
 * @param onApply - Called with draft values when the user clicks Done
 * @returns Step settings modal
 */
const StepSettingsModal = ({
  dialogId,
  idPrefix,
  title,
  enabled,
  action,
  statusField,
  settings,
  showBatchSettings = true,
  onApply,
}: StepSettingsModalProps) => {
  const [draft, setDraft] = useState<StepSettingsFormValues>({
    enabled,
    action,
    statusField,
    settings,
  });

  const resetDraft = () => {
    setDraft({ enabled, action, statusField, settings });
  };

  const handleSave = () => {
    onApply(draft);
    closeModal(dialogId);
  };

  const handleCancel = () => {
    resetDraft();
    closeModal(dialogId);
  };

  return (
    <Modal
      dialogId={dialogId}
      title={title}
      saveButtonText="Done"
      cancelText="Cancel"
      autoCloseOnSave={false}
      onSave={handleSave}
      onCancel={handleCancel}
      onClose={resetDraft}
    >
      <div className="flex flex-col gap-4 py-2">
        <StepSettingsCard
          idPrefix={idPrefix}
          enabled={draft.enabled}
          action={draft.action}
          statusField={draft.statusField}
          settings={draft.settings}
          showBatchSettings={showBatchSettings}
          onEnabledChange={(value) =>
            setDraft((current) => ({ ...current, enabled: value }))
          }
          onActionChange={(value) =>
            setDraft((current) => ({ ...current, action: value }))
          }
          onStatusFieldChange={(value) =>
            setDraft((current) => ({ ...current, statusField: value }))
          }
          onSettingChange={(key, value) =>
            setDraft((current) => ({
              ...current,
              settings: { ...current.settings, [key]: value },
            }))
          }
        />
      </div>
    </Modal>
  );
};

export default StepSettingsModal;
